<div class="sales-order-overview-tab grid grid-cols-1 lg:grid-cols-3 gap-6" [formGroup]="headerForm">
  <div class="lg:col-span-2 space-y-6">
    <!-- Client card -->
    <app-card-shell padding="lg">
      <section class="sales-order-overview-tab__section">
        <app-dynamic-editable-field
          class="sales-order-overview-tab__client-field block w-full"
          formControlName="clientId"
          layout="stacked"
          inputType="custom"
          editorClass="w-full"
          (updatedInput)="onCustomerFieldUpdated()"
        >
          <ng-template #labelTpl>
            <h2 class="sales-order-overview-tab__section-title">
              {{ 'COMPONENTS.SALES_ORDERS.SECTIONS.CLIENT' | translate }}
            </h2>
          </ng-template>

          <ng-template #contentTpl>
            @if (customerView; as customer) {
              <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
                <div class="flex flex-1 items-center gap-4">
                  <div
                    class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl text-lg font-bold text-white shadow-md"
                    [class.bg-gradient-to-br]="true"
                    [class.from-emerald-500]="customer.isOrganization"
                    [class.to-teal-600]="customer.isOrganization"
                    [class.from-blue-500]="customer.isContact"
                    [class.to-indigo-600]="customer.isContact"
                    aria-hidden="true"
                  >
                    {{ customer.title | abbreviation }}
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="m-0 truncate text-sm font-semibold text-neutral-900 dark:text-neutral-50">
                      {{ customer.title }}
                    </p>
                    <p class="m-0 text-xs text-neutral-500 dark:text-neutral-400">
                      {{ customer.typeLabelKey | translate }}
                    </p>
                  </div>
                </div>

                <div class="flex flex-1 items-center gap-3 sm:border-l sm:border-neutral-100 sm:pl-6 sm:dark:border-neutral-800">
                  <div class="min-w-0 flex-1">
                    <p class="m-0 text-xs uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
                      {{ 'COMPONENTS.SALES_ORDERS.SECTIONS.RESPONSIBLE' | translate }}
                    </p>
                    @if (customer.responsibleUserName) {
                      <div class="mt-1 flex items-center gap-2">
                        <div
                          class="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-400 to-violet-600 text-xs font-bold text-white"
                          aria-hidden="true"
                        >
                          {{ customer.responsibleUserName | abbreviation }}
                        </div>
                        <p class="m-0 text-sm font-medium text-neutral-900 dark:text-neutral-50">
                          {{ customer.responsibleUserName }}
                        </p>
                      </div>
                    } @else {
                      <p class="m-0 mt-1 text-sm italic text-neutral-400 dark:text-neutral-500">—</p>
                    }
                  </div>
                </div>
              </div>
            } @else {
              <p class="m-0 text-sm italic text-neutral-400 dark:text-neutral-500">
                {{ 'COMPONENTS.SALES_ORDERS.FIELDS.NO_CUSTOMER' | translate }}
              </p>
            }
          </ng-template>

          <ng-template #inputTpl let-value="value" let-update="update">
            <app-search
              customClass="form-search-input"
              [items]="clientOptions"
              [filterFn]="clientFilterFn"
              [formatter]="clientFormatter"
              [compareFn]="clientCompareFn"
              [placeholderText]="'COMPONENTS.SALES_ORDERS.PLACEHOLDERS.CLIENT' | translate"
              [errorMessage]="'COMPONENTS.SALES_ORDERS.ERRORS.INVALID_CLIENT' | translate"
              [ngModel]="value"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="update($event)"
              [resultTemplate]="clientOptionTemplate"
            ></app-search>

            <ng-template #clientOptionTemplate let-r="result" let-t="term">
              @if (r) {
                <div class="sales-order-overview-tab__client-option">
                  <span
                    class="sales-order-overview-tab__client-badge"
                    [class.sales-order-overview-tab__client-badge--organization]="r.type === 'organization'"
                    [class.sales-order-overview-tab__client-badge--contact]="r.type === 'contact'"
                  >
                    {{ r.typeLabelKey | translate }}
                  </span>

                  <div class="sales-order-overview-tab__client-text">
                    <ngb-highlight
                      class="sales-order-overview-tab__client-title"
                      highlightClass="hover-highlight"
                      [result]="r.title"
                      [term]="t"
                    ></ngb-highlight>
                    @if (r.subtitle) {
                      <ngb-highlight
                        class="sales-order-overview-tab__client-subtitle"
                        highlightClass="hover-highlight"
                        [result]="r.subtitle"
                        [term]="t"
                      ></ngb-highlight>
                    }
                  </div>
                </div>
              }
            </ng-template>
          </ng-template>
        </app-dynamic-editable-field>
      </section>
    </app-card-shell>

    <!-- Commercial info card -->
    <app-card-shell padding="lg">
      <section class="sales-order-overview-tab__section">
        <h2 class="sales-order-overview-tab__section-title">
          {{ 'COMPONENTS.SALES_ORDERS.SECTIONS.COMMERCIAL' | translate }}
        </h2>

        @if (headerErrorKey) {
          <p class="sales-order-overview-tab__error" role="alert">
            {{ headerErrorKey | translate }}
          </p>
        }

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <app-dynamic-editable-field
            class="sales-order-overview-tab__field"
            formControlName="status"
            layout="stacked"
            inputType="select"
            [selectItems]="statusSelectItems"
            (updatedInput)="onStatusFieldUpdated()"
          >
            <ng-template #labelTpl>
              <div class="sales-order-overview-tab__field-label">
                {{ 'COMPONENTS.SALES_ORDERS.FIELDS.STATUS' | translate }}
              </div>
            </ng-template>
            <ng-template #contentTpl>
              <div class="sales-order-overview-tab__field-value">
                @if (statusLabelKey) {
                  {{ statusLabelKey | translate }}
                } @else {
                  —
                }
              </div>
            </ng-template>
          </app-dynamic-editable-field>

          <app-dynamic-editable-field
            class="sales-order-overview-tab__field"
            formControlName="requestedDeliveryDate"
            layout="stacked"
            inputType="date"
            (updatedInput)="onRequestedDeliveryDateFieldUpdated()"
          >
            <ng-template #labelTpl>
              <div class="sales-order-overview-tab__field-label">
                {{ 'COMPONENTS.SALES_ORDERS.FIELDS.REQUESTED_DELIVERY_DATE' | translate }}
              </div>
            </ng-template>
            <ng-template #contentTpl>
              <div class="sales-order-overview-tab__field-value">
                @if (salesOrder?.requestedDeliveryDate) {
                  {{ salesOrder?.requestedDeliveryDate | date: 'dd.MM.yyyy' }}
                } @else {
                  —
                }
              </div>
            </ng-template>
          </app-dynamic-editable-field>
        </div>

        <app-dynamic-editable-field
          class="sales-order-overview-tab__field"
          formControlName="shortDescription"
          layout="stacked"
          inputType="text"
          [stretchLabelRow]="true"
          (updatedInput)="onShortDescriptionFieldUpdated()"
        >
          <ng-template #labelTpl>
            <div class="sales-order-overview-tab__field-label">
              {{ 'COMPONENTS.SALES_ORDERS.FIELDS.SHORT_DESCRIPTION' | translate }}
            </div>
          </ng-template>
          <ng-template #contentTpl>
            <div class="sales-order-overview-tab__field-value">
              @if (salesOrder?.shortDescription) {
                {{ salesOrder?.shortDescription }}
              } @else {
                —
              }
            </div>
          </ng-template>
        </app-dynamic-editable-field>

        <div class="sales-order-overview-tab__readonly-grid">
          <div class="sales-order-overview-tab__readonly-item">
            <p class="sales-order-overview-tab__readonly-label">
              {{ 'COMPONENTS.SALES_ORDERS.FIELDS.NUMBER' | translate }}
            </p>
            <p class="sales-order-overview-tab__readonly-value sales-order-overview-tab__readonly-value--mono">
              {{ salesOrder?.number || '—' }}
            </p>
          </div>
          <div class="sales-order-overview-tab__readonly-item">
            <p class="sales-order-overview-tab__readonly-label">
              {{ 'COMPONENTS.SALES_ORDERS.FIELDS.CREATED_DATE' | translate }}
            </p>
            <p class="sales-order-overview-tab__readonly-value">
              @if (salesOrder?.createdAt) {
                {{ salesOrder?.createdAt | date: 'dd.MM.yyyy' }}
              } @else {
                —
              }
            </p>
          </div>
          <div class="sales-order-overview-tab__readonly-item">
            <p class="sales-order-overview-tab__readonly-label">
              {{ 'COMPONENTS.SALES_ORDERS.TOTALS.NET' | translate }}
            </p>
            <p class="sales-order-overview-tab__readonly-value">
              {{ (salesOrder?.totals?.amountExcludingTax ?? 0) | number: '1.2-2' }}
            </p>
          </div>
          <div class="sales-order-overview-tab__readonly-item">
            <p class="sales-order-overview-tab__readonly-label">
              {{ 'COMPONENTS.SALES_ORDERS.TOTALS.TAX' | translate }}
            </p>
            <p class="sales-order-overview-tab__readonly-value">
              {{ (salesOrder?.totals?.totalTaxAmount ?? 0) | number: '1.2-2' }}
            </p>
          </div>
          <div class="sales-order-overview-tab__readonly-item sales-order-overview-tab__readonly-item--total">
            <p class="sales-order-overview-tab__readonly-label">
              {{ 'COMPONENTS.SALES_ORDERS.TOTALS.TOTAL' | translate }}
            </p>
            <p class="sales-order-overview-tab__readonly-value sales-order-overview-tab__readonly-value--strong">
              {{ (salesOrder?.totals?.amountIncludingTax ?? 0) | number: '1.2-2' }}
            </p>
          </div>
        </div>
      </section>
    </app-card-shell>
  </div>

  <div class="space-y-6">
    <!-- Info card -->
    <app-card-shell padding="lg">
      <section class="sales-order-overview-tab__section">
        <h2 class="sales-order-overview-tab__section-title">
          {{ 'COMPONENTS.SALES_ORDERS.SECTIONS.INFO' | translate }}
        </h2>

        <div class="space-y-3">
          <div>
            <p class="sales-order-overview-tab__readonly-label">
              {{ 'COMPONENTS.SALES_ORDERS.FIELDS.IDENTIFIER' | translate }}
            </p>
            <p class="sales-order-overview-tab__readonly-value sales-order-overview-tab__readonly-value--mono break-all">
              {{ salesOrder?.id || '—' }}
            </p>
          </div>
          <div>
            <p class="sales-order-overview-tab__readonly-label">
              {{ 'COMPONENTS.SALES_ORDERS.FIELDS.CREATED_DATE' | translate }}
            </p>
            <p class="sales-order-overview-tab__readonly-value">
              @if (salesOrder?.createdAt) {
                {{ salesOrder?.createdAt | date: 'dd.MM.yyyy' }}
              } @else {
                —
              }
            </p>
          </div>
          <div>
            <p class="sales-order-overview-tab__readonly-label">
              {{ 'COMPONENTS.SALES_ORDERS.FIELDS.UPDATED_AT' | translate }}
            </p>
            <p class="sales-order-overview-tab__readonly-value">
              @if (salesOrder?.lastModifiedDateTime) {
                {{ salesOrder?.lastModifiedDateTime | date: 'dd.MM.yyyy' }}
              } @else {
                —
              }
            </p>
          </div>
          <div>
            <p class="sales-order-overview-tab__readonly-label">
              {{ 'COMPONENTS.SALES_ORDERS.FIELDS.SOURCE' | translate }}
            </p>
            @if (integrationView) {
              <span
                class="sales-order-overview-tab__source-badge"
                [ngClass]="'sales-order-overview-tab__source-badge--' + integrationView.sourceBadgeVariant"
              >
                {{ integrationView.sourceBadgeKey | translate }}
              </span>
            } @else {
              <p class="sales-order-overview-tab__readonly-value">—</p>
            }
          </div>
        </div>
      </section>
    </app-card-shell>

    <!-- Quick actions card -->
    <app-card-shell padding="lg">
      <section class="sales-order-overview-tab__section">
        <h2 class="sales-order-overview-tab__section-title">
          {{ 'COMPONENTS.SALES_ORDERS.SECTIONS.QUICK_ACTIONS' | translate }}
        </h2>

        <div class="space-y-2">
          <button
            type="button"
            class="sales-order-overview-tab__quick-action"
            [disabled]="isQuickActionLocked"
            (click)="onIssueInvoice()"
          >
            <svg class="h-4 w-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {{ 'COMPONENTS.SALES_ORDERS.QUICK_ACTIONS.ISSUE_INVOICE' | translate }}
          </button>

          <button
            type="button"
            class="sales-order-overview-tab__quick-action"
            [disabled]="isQuickActionLocked"
            (click)="onCreateShipment()"
          >
            <svg class="h-4 w-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7a2 2 0 012-2h9a2 2 0 012 2v8H3V7zm13 3h3l2 3v2a2 2 0 01-2 2h-1m-2 0H5" />
              <circle cx="7.5" cy="17.5" r="1.5" stroke-width="2" />
              <circle cx="17.5" cy="17.5" r="1.5" stroke-width="2" />
            </svg>
            {{ 'COMPONENTS.SALES_ORDERS.QUICK_ACTIONS.CREATE_SHIPMENT' | translate }}
          </button>

          <button
            type="button"
            class="sales-order-overview-tab__quick-action"
            [disabled]="isDuplicating"
            (click)="onDuplicateOrder()"
          >
            <svg class="h-4 w-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            {{ 'COMPONENTS.SALES_ORDERS.QUICK_ACTIONS.DUPLICATE_ORDER' | translate }}
          </button>
        </div>
      </section>
    </app-card-shell>
  </div>
</div>

<app-reusable-modal
  [isOpen]="isStatusConfirmationOpen"
  [panelClass]="'max-w-lg'"
  [contentScoped]="true"
  [title]="statusConfirmationTitleKey | translate"
  (closed)="closeStatusConfirmation()"
>
  <section class="sales-order-confirmation">
    <p class="sales-order-confirmation__prompt">
      {{ statusConfirmationPromptKey | translate }}
    </p>

    <div class="sales-order-confirmation__actions">
      <button
        type="button"
        class="form-secondary-btn"
        [disabled]="isSavingHeader"
        (click)="closeStatusConfirmation()"
      >
        {{ 'BUTTONS.BACK' | translate }}
      </button>
      <button
        type="button"
        [ngClass]="statusConfirmationActionClass"
        [disabled]="isSavingHeader"
        (click)="confirmStatusChange()"
      >
        @if (isSavingHeader) {
          {{ statusConfirmationLoadingKey | translate }}
        } @else {
          {{ statusConfirmationActionKey | translate }}
        }
      </button>
    </div>
  </section>
</app-reusable-modal>
